import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTriggerLandRegisterDownload } from '../../hooks/reactQuerys/LandMarkingQueries';
import { searchOptionLandRegisterSchema } from '../../Interfaces/Helper/ISearchOptionLandRegister';
import {
  IFromRechercheResultEntry,
  IRechercheBlattInput,
  IRechercheClientMatch,
  IRechercheOwnerInput,
  useFromRecherche,
  useRechercheClientMatch,
} from '../../hooks/reactQuerys/GrundbuchblattQueries';
import { ILegalTransactionGrundbuchblattLink } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { useAlert } from '../../context/AlertContext';

/** Ein Eigentümer aus der Recherche (nur Name). */
export interface IRechercheOwner {
  LastName: string;
  FirstName: string;
  Birthday: string;
}

/** Ein zusammengefasster Recherche-Treffer (je Blatt+Flur+Flurstück). */
export interface IRechercheGroupedEntry {
  SheetLandRegister: string;
  Parcel: string; // Flur
  LandParcel: string; // Flurstück
  StreetSearch: string; // Lage
  gemarkungName?: string;
  idCourt: number | null;
  idAdministrativeUnit: number | null;
  owners: IRechercheOwner[];
}

interface IProps {
  /** Recherche-Treffer (je Blatt+Flur+Flurstück gruppiert). */
  entries: IRechercheGroupedEntry[];
  /**
   * Vorgang-Kontext: Ist der Callback gesetzt, kann „dem Vorgang zuordnen"
   * gewählt werden. Die eigentliche Verknüpfung übernimmt der Aufrufer beim
   * Speichern des Vorgangs (GrundbuchblattArray) — hier wird NICHT verknüpft.
   */
  onAssigned?: (links: ILegalTransactionGrundbuchblattLink[]) => void;
  /** #1175: Zugang (Bundesland-Login) der Recherche — ermöglicht das direkte
   *  Ziehen des GB-Auszugs für ein angelegtes Blatt aus der Recherche heraus. */
  idFederalStateLogin?: number | null;
  /**
   * Aktenzeichen aus der Recherche-Maske. Ein frisch angelegtes Blatt hängt an
   * keinem Vorgang, aus dem das Backend etwas ableiten könnte — ohne diesen
   * Wert bliebe für das Portal-Pflichtfeld nur die Blattnummer.
   */
  searchCaseNumber?: string;
}

const ownerKey = (o: IRechercheOwner) =>
  `${o.LastName}|${o.FirstName}|${o.Birthday}`.toLowerCase();

const ownerLabel = (o: IRechercheOwner) =>
  `${o.LastName} ${o.FirstName}`.trim() +
  (o.Birthday ? ` (* ${o.Birthday})` : '');

interface IOwnerDecision {
  action: 'use' | 'create';
  idLegalClient?: number;
}

/** Pro Eigentümer: Mandantensuche (Score/Match-Felder) + Verwenden/Neu anlegen. */
const RechercheOwnerDecision: React.FC<{
  owner: IRechercheOwner;
  decision?: IOwnerDecision;
  onChange: (decision: IOwnerDecision) => void;
}> = ({ owner, decision, onChange }) => {
  const { matchClients, isMatchingClients } = useRechercheClientMatch();
  const [matches, setMatches] = useState<IRechercheClientMatch[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    matchClients({
      LastName: owner.LastName,
      FirstName: owner.FirstName,
      Birthday: owner.Birthday,
    })
      .then(res => {
        if (cancelled) return;
        const m = res.matches ?? [];
        setMatches(m);
        setLoaded(true);
        // Vorauswahl: eindeutiger, sehr sicherer Treffer -> verwenden; sonst neu anlegen.
        if (!decision) {
          if (m.length > 0 && m[0].score >= 70) {
            onChange({ action: 'use', idLegalClient: m[0].idLegalClient });
          } else {
            onChange({ action: 'create' });
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setMatches([]);
        setLoaded(true);
        if (!decision) onChange({ action: 'create' });
      });
    return () => {
      cancelled = true;
    };
  }, [owner.LastName, owner.FirstName, owner.Birthday]);

  return (
    <Paper variant='outlined' sx={{ p: 1.5 }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <Typography variant='subtitle2'>
          Eigentümer: {ownerLabel(owner)}
        </Typography>
        {isMatchingClients && <CircularProgress size={16} />}
      </Stack>

      {loaded && matches.length === 0 && (
        <Alert severity='info' variant='outlined' sx={{ mb: 1 }}>
          Kein passender Mandant gefunden — wird neu angelegt.
        </Alert>
      )}

      {matches.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1, maxHeight: 200, overflowY: 'auto' }}>
          {matches.map(m => {
            const selected =
              decision?.action === 'use' &&
              decision.idLegalClient === m.idLegalClient;
            return (
              <Paper
                key={m.idLegalClient}
                variant='outlined'
                onClick={() =>
                  onChange({ action: 'use', idLegalClient: m.idLegalClient })
                }
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                }}
              >
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  flexWrap='wrap'
                  useFlexGap
                >
                  <Typography variant='body2' sx={{ flexGrow: 1 }}>
                    {[m.FirstName, m.LastName].filter(Boolean).join(' ')}
                    {m.Email ? ` · ${m.Email}` : ''}
                    {` · Nr. ${m.idLegalClient}`}
                  </Typography>
                  <Chip
                    size='small'
                    label={`Übereinstimmung ${m.score}%`}
                    color={m.score >= 70 ? 'success' : 'default'}
                  />
                  {m.matchedFields.map(f => (
                    <Chip key={f} size='small' variant='outlined' label={f} />
                  ))}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <FormControlLabel
        control={
          <Checkbox
            size='small'
            checked={decision?.action === 'create'}
            onChange={() => onChange({ action: 'create' })}
          />
        }
        label='Neuen Mandanten anlegen'
      />
    </Paper>
  );
};

/**
 * Aus den Eigentümerrecherche-Treffern werden – nach Blattnummer gruppiert –
 * Grundbuchblätter angelegt (Bestandsverzeichnis + Eigentümer in Abt. I). Pro
 * Blatt wählbar: anlegen und/oder dem Vorgang als Vertragsgegenstand zuordnen.
 * Anlegen ohne Zuordnung ist möglich (z. B. tangierte Grundstücke erfassen).
 */
export const BlattAnlageAusRecherche: React.FC<IProps> = ({
  entries,
  onAssigned,
  idFederalStateLogin,
  searchCaseNumber,
}) => {
  const { showAlert } = useAlert();
  const { downloadLandRegister, isDownloadingLandMarkings } =
    useTriggerLandRegisterDownload();
  // #1175: Passwort-Prompt für den direkten GB-Abruf eines angelegten Blatts.
  const [gbPrompt, setGbPrompt] = useState<{
    open: boolean;
    idGrundbuchblatt: number | null;
    password: string;
  }>({ open: false, idGrundbuchblatt: null, password: '' });

  const runGbDownload = async () => {
    const gid = gbPrompt.idGrundbuchblatt;
    const pw = gbPrompt.password;
    setGbPrompt({ open: false, idGrundbuchblatt: null, password: '' });
    if (!gid) return;
    try {
      await downloadLandRegister(
        searchOptionLandRegisterSchema.parse({
          idGrundbuchblatt: gid,
          idFederalStateLogin: idFederalStateLogin ?? null,
          Password: pw,
          SearchCaseNumber: searchCaseNumber ?? '',
          SelectedDownload: ['GB'],
        })
      );
      showAlert({
        severity: 'success',
        text: 'Grundbuchauszug erfolgreich bezogen.',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Der Grundbuchauszug konnte nicht bezogen werden. Bitte Zugang, Passwort und die Verfügbarkeit des Grundbuchs prüfen.',
      });
    }
  };
  const { createBlaetterFromRecherche, isCreatingBlaetter } =
    useFromRecherche();
  // Zuordnung nur möglich, wenn ein Vorgang-Kontext (Callback) vorhanden ist.
  const canAssign = !!onAssigned;

  // 1) Nach Blattnummer gruppieren: Flurstücke sammeln, Eigentümer aggregieren.
  const blattGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        Blattnummer: string;
        gemarkungName: string;
        idCourt: number | null;
        idAdministrativeUnit: number | null;
        flurstuecke: {
          Flur: string;
          Flurstuecksnummer: string;
          Lage: string;
          idAdministrativeUnit: number | null;
        }[];
        owners: IRechercheOwner[];
        ownerKeys: Set<string>;
      }
    >();
    entries.forEach(e => {
      const nr = (e.SheetLandRegister || '').trim();
      if (!nr) return;
      let g = map.get(nr);
      if (!g) {
        g = {
          Blattnummer: nr,
          gemarkungName: e.gemarkungName ?? '',
          idCourt: e.idCourt ?? null,
          idAdministrativeUnit: e.idAdministrativeUnit ?? null,
          flurstuecke: [],
          owners: [],
          ownerKeys: new Set(),
        };
        map.set(nr, g);
      }
      g.flurstuecke.push({
        Flur: e.Parcel || '',
        Flurstuecksnummer: e.LandParcel || '',
        Lage: e.StreetSearch || '',
        idAdministrativeUnit: e.idAdministrativeUnit ?? null,
      });
      e.owners.forEach(o => {
        const k = ownerKey(o);
        if (!g!.ownerKeys.has(k)) {
          g!.ownerKeys.add(k);
          g!.owners.push(o);
        }
      });
    });
    return Array.from(map.values());
  }, [entries]);

  // Eindeutige Eigentümer über alle Blätter (eine Entscheidung je Person).
  const distinctOwners = useMemo(() => {
    const seen = new Map<string, IRechercheOwner>();
    blattGroups.forEach(g =>
      g.owners.forEach(o => {
        const k = ownerKey(o);
        if (!seen.has(k)) seen.set(k, o);
      })
    );
    return Array.from(seen.entries()); // [key, owner][]
  }, [blattGroups]);

  // Zustand: pro Blatt anlegen/zuordnen, pro Eigentümer die Entscheidung.
  const [anlegen, setAnlegen] = useState<Record<string, boolean>>({});
  const [zuordnen, setZuordnen] = useState<Record<string, boolean>>({});
  const [ownerDecisions, setOwnerDecisions] = useState<
    Record<string, IOwnerDecision>
  >({});

  useEffect(() => {
    const a: Record<string, boolean> = {};
    const z: Record<string, boolean> = {};
    blattGroups.forEach(g => {
      a[g.Blattnummer] = true;
      z[g.Blattnummer] = canAssign;
    });
    setAnlegen(a);
    setZuordnen(z);
  }, [blattGroups, canAssign]);

  const [done, setDone] = useState<IFromRechercheResultEntry[] | null>(null);

  const onSubmit = async () => {
    const blaetter: IRechercheBlattInput[] = blattGroups
      .filter(g => anlegen[g.Blattnummer])
      .map(g => {
        const owners: IRechercheOwnerInput[] = g.owners.map(o => {
          const d = ownerDecisions[ownerKey(o)];
          if (d?.action === 'use' && d.idLegalClient) {
            return {
              action: 'use',
              idLegalClient: d.idLegalClient,
              share: null,
            };
          }
          return {
            action: 'create',
            FirstName: o.FirstName || null,
            LastName: o.LastName || null,
            Birthday: o.Birthday || null,
            share: null,
          };
        });
        return {
          Blattnummer: g.Blattnummer,
          idCourt: g.idCourt,
          idAdministrativeUnit: g.idAdministrativeUnit,
          assignToVorgang: canAssign && !!zuordnen[g.Blattnummer],
          grundstuecke: [
            {
              idGrundstueck: -1,
              lfdNr: '1',
              bisherigeLfdNr: '',
              FlurstueckArray: g.flurstuecke,
              AddressArray: [],
            } as any,
          ],
          owners,
        };
      });

    if (blaetter.length === 0) {
      showAlert({
        severity: 'warning',
        text: 'Bitte mindestens ein Blatt zum Anlegen auswählen.',
      });
      return;
    }

    try {
      const res = await createBlaetterFromRecherche({ blaetter });
      const created = res.created ?? [];
      const ok = created.filter(c => c.idGrundbuchblatt).length;
      // Zugeordnete Blätter an den Aufrufer geben — die Verknüpfung nimmt er in die
      // Vorgang-GrundbuchblattArray auf und persistiert sie beim Vorgang-Speichern.
      const assignedLinks = created
        .filter(c => c.assignToVorgang && c.link)
        .map(c => c.link as ILegalTransactionGrundbuchblattLink);
      if (canAssign && assignedLinks.length > 0) onAssigned?.(assignedLinks);
      setDone(created);
      showAlert({
        severity: 'success',
        text: `${ok} Blatt/Blätter angelegt${
          canAssign && assignedLinks.length > 0
            ? `; ${assignedLinks.length} wird/werden beim Speichern des Vorgangs zugeordnet`
            : ''
        }.`,
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Die Blätter konnten nicht angelegt werden.',
      });
    }
  };

  if (blattGroups.length === 0) {
    return (
      <Alert severity='info'>
        Keine Treffer mit Blattnummer — es können keine Blätter angelegt werden.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        Aus den Recherche-Treffern werden – nach Blattnummer gruppiert –
        Grundbuchblätter angelegt (Bestandsverzeichnis + Eigentümer in Abteilung
        I).
        {canAssign
          ? ' Wählen Sie zusätzlich, welche dem Vorgang als Vertragsgegenstand zugeordnet werden.'
          : ' (Ohne Vorgangskontext werden die Blätter nur angelegt, nicht zugeordnet.)'}
      </Typography>

      {/* Eigentümer-Entscheidungen (eine je Person, blattübergreifend) */}
      {distinctOwners.length > 0 && (
        <Box>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Eigentümer (Abteilung I)
          </Typography>
          <Stack spacing={1}>
            {distinctOwners.map(([k, o]) => (
              <RechercheOwnerDecision
                key={k}
                owner={o}
                decision={ownerDecisions[k]}
                onChange={d => setOwnerDecisions(prev => ({ ...prev, [k]: d }))}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Divider />

      {/* Blätter (nach Blattnummer) */}
      <Box>
        <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
          Grundbuchblätter
        </Typography>
        <Stack spacing={1.5}>
          {blattGroups.map(g => (
            <Paper key={g.Blattnummer} variant='outlined' sx={{ p: 1.5 }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                flexWrap='wrap'
                useFlexGap
                sx={{ mb: 1 }}
              >
                <Typography variant='subtitle2'>
                  Blatt {g.Blattnummer}
                  {g.gemarkungName ? ` · Gemarkung ${g.gemarkungName}` : ''}
                </Typography>
                <Stack direction='row' spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={!!anlegen[g.Blattnummer]}
                        onChange={(_, v) =>
                          setAnlegen(p => ({ ...p, [g.Blattnummer]: v }))
                        }
                      />
                    }
                    label='Blatt anlegen'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        disabled={!canAssign || !anlegen[g.Blattnummer]}
                        checked={canAssign && !!zuordnen[g.Blattnummer]}
                        onChange={(_, v) =>
                          setZuordnen(p => ({ ...p, [g.Blattnummer]: v }))
                        }
                      />
                    }
                    label='dem Vorgang zuordnen'
                  />
                </Stack>
              </Stack>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Flur</TableCell>
                    <TableCell>Flurstück</TableCell>
                    <TableCell>Lage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.flurstuecke.map((f, i) => (
                    <TableRow key={`${g.Blattnummer}-${i}`}>
                      <TableCell>{f.Flur || '–'}</TableCell>
                      <TableCell>{f.Flurstuecksnummer || '–'}</TableCell>
                      <TableCell>{f.Lage || '–'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {g.owners.length > 0 && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Eigentümer: {g.owners.map(ownerLabel).join(', ')}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </Box>

      {done && (
        <Stack spacing={1}>
          <Alert severity='success' variant='outlined'>
            {done
              .map(
                c =>
                  `Blatt ${c.Blattnummer}: ${
                    c.idGrundbuchblatt
                      ? `angelegt (#${c.idGrundbuchblatt})${
                          c.assignToVorgang ? ', wird zugeordnet' : ''
                        }`
                      : c.fehler || 'Fehler'
                  }`
              )
              .join(' · ')}
          </Alert>
          {/* #1175: GB-Auszug direkt aus der Recherche ziehen (angelegtes Blatt). */}
          {idFederalStateLogin != null &&
            done.some(c => c.idGrundbuchblatt) && (
              <Paper variant='outlined' sx={{ p: 1.5 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Grundbuchauszug direkt ziehen
                </Typography>
                <Stack spacing={0.75}>
                  {done
                    .filter(c => c.idGrundbuchblatt)
                    .map(c => (
                      <Stack
                        key={c.idGrundbuchblatt}
                        direction='row'
                        spacing={1}
                        alignItems='center'
                      >
                        <Typography variant='body2' sx={{ flexGrow: 1 }}>
                          Blatt {c.Blattnummer || '(Entwurf)'} (#
                          {c.idGrundbuchblatt})
                        </Typography>
                        <Button
                          size='small'
                          variant='outlined'
                          disabled={isDownloadingLandMarkings}
                          onClick={() =>
                            setGbPrompt({
                              open: true,
                              idGrundbuchblatt: c.idGrundbuchblatt as number,
                              password: '',
                            })
                          }
                        >
                          GB-Auszug ziehen
                        </Button>
                      </Stack>
                    ))}
                </Stack>
              </Paper>
            )}
        </Stack>
      )}

      <Box sx={{ textAlign: 'right' }}>
        <Button
          variant='contained'
          onClick={onSubmit}
          disabled={isCreatingBlaetter}
          startIcon={
            isCreatingBlaetter ? (
              <CircularProgress size={16} color='inherit' />
            ) : undefined
          }
        >
          Blätter anlegen
        </Button>
      </Box>

      {/* #1175: Passwort-Prompt für den direkten GB-Abruf. */}
      <Dialog
        open={gbPrompt.open}
        onClose={() =>
          setGbPrompt({ open: false, idGrundbuchblatt: null, password: '' })
        }
      >
        <DialogTitle>Passwort für den Grundbuch-Zugang</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            type='password'
            label='Passwort'
            fullWidth
            value={gbPrompt.password}
            onChange={e =>
              setGbPrompt(p => ({ ...p, password: e.target.value }))
            }
            onKeyDown={e => {
              if (e.key === 'Enter' && gbPrompt.password) runGbDownload();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setGbPrompt({ open: false, idGrundbuchblatt: null, password: '' })
            }
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            disabled={!gbPrompt.password || isDownloadingLandMarkings}
            onClick={runGbDownload}
          >
            GB-Auszug ziehen
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
